import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AnalyticsTrigger, Button, Grid, GridColumn, Text } from 'telia-front-react';
import { removePlusAndEstonianAreaCode } from '../../stores/CountryCallingCodeStore';
import { useStores } from '../../stores/RootStore';
import { scrollUpToElement } from '../../util/HtmlUtil';
import { previewTime, renderLabelAndValue } from '../common/FormatUtil';
import ReservationErrorNotice from '../common/ReservationErrorNotice';

const RequestCallbackPreview = observer((): React.JSX.Element => {
  const {
    callbackStore: { callbackCreation, callbackCreationResult, createReservation },
    countryCallingCodeStore: { isForeignCodeSelected, countryCallingCode }
  } = useStores();
  const { translate } = React.useContext(TranslationsContext);
  const showError = ['ERROR', 'INVALID_INPUT'].includes(callbackCreationResult.status);
  const { isMobileOrTablet } = useIsMobileHook();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (showError) {
      scrollUpToElement('reservation-error-notice', isMobileOrTablet);
    }
  }, [showError]);

  const resetCallbackCreationTimeIfInputInvalid = (): void => {
    if (callbackCreationResult.status === 'INVALID_INPUT') {
      callbackCreation.time = null;
    }
  };

  return (
    <Text spacing="double">
      <b>{translate('reservation.requestCallback.preview')}</b>
      {showError && <ReservationErrorNotice status={callbackCreationResult.status} errors={callbackCreationResult.errors} />}
      {renderLabelAndValue(
        translate('reservation.requestCallback.preview.topic'),
        translate(`reservation.requestCallback.topic.${callbackCreation.topic.toLowerCase()}`)
      )}
      {!!callbackCreation.description && renderLabelAndValue(translate('reservation.preview.description'), callbackCreation.description)}
      {renderLabelAndValue(translate('reservation.requestCallback.name'), callbackCreation.name)}
      {renderLabelAndValue(
        translate('reservation.requestCallback.language.label'),
        translate(`reservation.requestCallback.language.${callbackCreation.language.toLowerCase()}`)
      )}
      {renderLabelAndValue(translate('form.fields.phone'), `${countryCallingCode} ${removePlusAndEstonianAreaCode(callbackCreation.phone)}`)}
      {isForeignCodeSelected && callbackCreation.email && renderLabelAndValue(translate('form.fields.email'), callbackCreation.email)}
      {renderLabelAndValue(translate('reservation.preview.time'), previewTime(callbackCreation.time, translate('reservation.preview.timePrefix')))}
      <Grid align={['middle-xs']}>
        <GridColumn width={['xs-6']}>
          <AnalyticsTrigger trigger={{ id: 'Button - Change callback request details', callback: 'onClick' }}>
            <Button
              iconLeft="#arrow-left"
              variant="secondary"
              onClick={() => {
                runInAction(() => resetCallbackCreationTimeIfInputInvalid());
                runInAction(() => (callbackCreationResult.status = 'BEING_FILLED'));
              }}
              text={translate('reservation.preview.change')}
              link={true}
            />
          </AnalyticsTrigger>
        </GridColumn>
        <GridColumn width={['xs-6']}>
          <AnalyticsTrigger trigger={{ id: 'Button - Request callback', callback: 'onClick' }}>
            <Button
              iconRight="#arrow-right"
              onClick={() => {
                createReservation(callbackCreation.branchId, callbackCreation);
              }}
              text={translate('reservation.requestCallback.submit')}
              disabled={callbackCreationResult.status === 'SENDING' || showError}
            />
          </AnalyticsTrigger>
        </GridColumn>
      </Grid>
    </Text>
  );
});

export default RequestCallbackPreview;
